/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Client models for an organisation's own e-mail sender. They mirror the
 * `OrganizationEmailConfig*Dto` classes of `@product-passport/organization/dto`.
 */

/** `smtp` = the customer's own mail server; `ses` = the platform e-mail service. */
export type EmailProvider = 'smtp' | 'ses';
/** Transport security of the SMTP connection. */
export type SmtpSecurity = 'starttls' | 'tls' | 'none';
/** What the platform e-mail service verifies; the UI only offers `domain`. */
export type SesIdentityType = 'email' | 'domain';
/** Only `verified` configurations are used for notifications. */
export type EmailVerificationStatus = 'pending' | 'verified' | 'failed';

const SMTP_PORT_RELAY = 25;
const SMTP_PORT_SMTPS = 465;
/** Message submission with STARTTLS (RFC 6409) — the default for new configurations. */
export const SMTP_PORT_SUBMISSION = 587;
const SMTP_PORT_SUBMISSION_ALT = 2525;

/** Ports offered in the SMTP form, in display order. */
export const SMTP_PORTS = [
  SMTP_PORT_RELAY,
  SMTP_PORT_SMTPS,
  SMTP_PORT_SUBMISSION,
  SMTP_PORT_SUBMISSION_ALT,
] as const;

/** DNS record the customer publishes to prove ownership of the sender domain. */
export interface DnsRecord {
  name: string;
  type: 'CNAME';
  value: string;
}

/** Read model returned by the backend — never contains credentials. */
export interface EmailSenderConfig {
  enabled: boolean;
  provider: EmailProvider;
  fromAddress: string;
  fromName: string;
  supportEmail?: string;
  smtp?: {
    host: string;
    port: number;
    security: SmtpSecurity;
    username?: string;
    hasPassword: boolean;
  };
  ses?: {
    identityType: SesIdentityType;
    identity: string;
    dnsRecords?: DnsRecord[];
  };
  verificationStatus: EmailVerificationStatus;
  verifiedAt?: string;
  verificationMessage?: string;
  updatedAt?: string;
  lastError?: string;
  lastErrorAt?: string;
}

/** Write model sent to the backend — secrets are optional (omit to keep stored values). */
export interface EmailSenderConfigRequest {
  enabled: boolean;
  provider: EmailProvider;
  fromAddress: string;
  fromName: string;
  supportEmail?: string;
  smtp?: {
    host: string;
    port: number;
    security: SmtpSecurity;
    username?: string;
    password?: string;
  };
  ses?: {
    identityType: SesIdentityType;
  };
}
